<template>
  <div>
    <div v-if="showFilters" class="mt-3 text-left p-3 bg-gray-100 rounded-md !shadow">
      <SearchForm v-model="search.query" @search="loadData" @clear="clearFilters" />

      <div class="flex gap-4 flex-wrap pt-3 items-center justify-between">
        <div class="flex gap-4 flex-wrap">
          <div class="flex flex-row">
            <div class="rounded-l-md shadow-sm p-2">
              Start
            </div>
            <Input label="Start" class="rounded-md shadow-sm" type="date" v-model="query.startDate"
              @change="loadData" />
          </div>

          <div class="flex flex-row">
            <div class="rounded-l-md shadow-sm p-2">
              End
            </div>
            <Input label="End" class="rounded-md shadow-sm" type="date" v-model="query.endDate" @change="loadData" />
          </div>
        </div>
        <Button class="bg-white rounded-md text-gray-600 font-normal shadow-sm h-10 p-2 hover:!bg-gray-50" :size="'sm'"
          :disabled="downloadingCSV" @click="downloadCSV">
          {{ downloadingCSV ? 'Exporting...' : 'Export to CSV' }}
        </Button>
      </div>
    </div>
    <div v-if="studentDbId" class="flex items-center justify-between mt-3 mb-3">
      <div class="flex flex-row">
        <div class="rounded-l-md shadow-sm p-2">
          Date
        </div>

        <Input label="Date" class="rounded-md shadow-sm" type="date" v-model="studentDate"
          @input="studentDateChanged" />
      </div>

      <label class="flex items-center gap-2">
        <input type="checkbox" v-model="showPastRides" @change="showPastRidesChanged">
        Show past rides
      </label>
    </div>
    <div v-if="showPagination && pages.total" class="flex justify-center pt-4">
      <Pagination :pages="pages" :to="$route.path" />
    </div>

    <div class="mt-6 space-y-2 pb-6">
      <Table>
        <template #columns>
          <th v-for="tableSort in tableSorts.filter(
            sort => !sort.internalOnly || isInternal
          )" :key="tableSort.name">
            <SortTable :name="tableSort.name" :disabled="tableSort.disabled" :selectedSort="selectedSort"
              @setSelectedSort="setSelectedSort">
              {{ tableSort.text }}
            </SortTable>
          </th>
        </template>
        <template #rows>
          <OneTimeRideItem v-for="item in oneTimeRides" :key="item.id" :item="item" :isInternal="isInternal" />
        </template>
      </Table>

      <div v-if="!loadingData && oneTimeRides.length === 0" class="text-center p-6 text-gray-500">
        No one time rides found.
      </div>
    </div>

    <div v-if="showPagination && oneTimeRides.length > 0" class="flex justify-center pb-4">
      <Pagination :pages="pages" :to="$route.path" />
    </div>
  </div>
</template>

<script>
import Input from './Input.vue'
import OneTimeRideItem from './items/OneTimeRideItem.vue'
import SortTable from './SortTable.vue'
import Table from './Table.vue'
import Pagination from './Pagination.vue'
import SearchForm from './Search/SearchForm.vue'
import Button from './Button.vue'

import oneTimeRideListService from '../services/pages/oneTimeRideList.js'
import user from '../services/user/user.js'

export default {
  name: 'OneTimeRideList',

  components: {
    Input,
    OneTimeRideItem,
    Button,
    SortTable,
    Table,
    Pagination,
    SearchForm,
  },

  props: {
    studentDbId: {
      type: [Number, String],
      default: undefined,
    },

    showFilters: {
      type: Boolean,
      default: true,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loadingData: false,
      oneTimeRides: [],
      pages: {},
      isInternal: false,
      studentDate: '',
      showPastRides: false,
      downloadingCSV: false,

      search: {
        query: '',
      },

      query: {
        startDate: undefined,
        endDate: undefined,
      },

      selectedSort: {
        name: 'tripDate',
        descending: true,
      },

      tableSorts: [
        {
          name: 'tripDate',
          text: 'Trip Date',
          disabled: false,
        },
        {
          name: 'internalId',
          text: 'Internal ID',
          disabled: false,
          internalOnly: true,
        },
        {
          name: 'studentDbId',
          text: 'Student ID',
          disabled: false,
        },
        {
          name: 'firstName',
          text: 'Student',
          disabled: false,
        },
        {
          name: 'district',
          text: 'District',
          disabled: false,
        },
        {
          name: 'direction',
          text: 'Direction',
          disabled: true,
        },
        {
          name: 'school',
          text: 'School',
          disabled: true,
        },
        {
          name: 'pickupLocation',
          text: 'Pickup Location',
          disabled: true,
        },
        {
          name: 'dropoffLocation',
          text: 'Dropoff Location',
          disabled: true,
        },
        {
          name: 'vendor',
          text: 'Vendor',
          disabled: false,
        },
      ],
    }
  },

  methods: {
    async loadData() {
      if (this.loadingData) return

      this.loadingData = true

      try {
        const currentUser = await user.get()

        this.isInternal = currentUser.can('view_internal_cst_data')

        let startDate = this.query.startDate
        let endDate = this.query.endDate

        if (this.studentDbId) {
          if (this.studentDate) {
            startDate = this.studentDate
            endDate = this.studentDate
          }
          else if (!this.showPastRides) {
            const today = new Date()

            startDate = [
              today.getFullYear(),
              String(today.getMonth() + 1).padStart(2, '0'),
              String(today.getDate()).padStart(2, '0'),
            ].join('-')

            endDate = undefined
          }
          else {
            startDate = undefined
            endDate = undefined
          }
        }

        const params = {
          studentId: this.studentDbId,
          startDate,
          endDate,
          page: this.$route.query.page,
          search: this.search.query,
          sortBy: this.selectedSort.name
            ? `${this.selectedSort.descending ? '-' : ''}${this.selectedSort.name}`
            : '',
        }

        const response =
          await oneTimeRideListService.getOneTimeRideDataFromServer(params)

        this.pages = response.data.pages
        this.oneTimeRides = response.data.content
      }
      catch (error) {
        console.error('Error fetching OneTimeRide data:', error)
        this.oneTimeRides = []
      }
      finally {
        this.loadingData = false
      }
    },

    async downloadCSV() {
      if (this.downloadingCSV) return

      this.downloadingCSV = true

      try {
        let startDate = this.query.startDate
        let endDate = this.query.endDate

        if (this.studentDbId) {
          if (this.studentDate) {
            startDate = this.studentDate
            endDate = this.studentDate
          }
          else if (!this.showPastRides) {
            const today = new Date()

            startDate = [
              today.getFullYear(),
              String(today.getMonth() + 1).padStart(2, '0'),
              String(today.getDate()).padStart(2, '0'),
            ].join('-')

            endDate = undefined
          }
          else {
            startDate = undefined
            endDate = undefined
          }
        }

        const params = {
          studentDbId: this.studentDbId,
          startDate,
          endDate,
          search: this.search.query,
          sortBy: this.selectedSort.name
            ? `${this.selectedSort.descending ? '-' : ''}${this.selectedSort.name}`
            : '',
        }
        const response = await oneTimeRideListService.downloadOneTimeRideCSV(params)

        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', 'otr_submissions.csv')

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
      }
      catch (error) {
        console.error('Error downloading OneTimeRide CSV:', error)
      }
      finally {
        this.downloadingCSV = false
      }
    },

    setSelectedSort(value) {
      this.selectedSort = value
      this.loadData()
    },

    studentDateChanged() {
      this.loadData()
    },

    showPastRidesChanged() {
      this.loadData()
    },

    clearFilters() {
      this.search.query = ''
      this.query.startDate = undefined
      this.query.endDate = undefined
      this.loadData()
    },
  },

  watch: {
    '$route.query.page'() {
      this.loadData()
    },

    studentDbId() {
      this.loadData()
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>
